import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSelect, useSwitch } from '@chilecompra/react-kit'
import { DataTable, Select, Switch } from '@chilecompra/react-kit/components'
import { CURRENCY_VALUES } from '../../config/settings/constants'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import { formatAmount } from '../../modules/utils/formatters'
import generateYearsBetween from '../../modules/utils/years'
import { onGetBuyersProvidersSizeThunk } from './BuyersProvidersSizeSection.actions'
import ColumnsChart from '../../components/Charts/ColumnsChart'
import RegionalAmountsTradedNationalLevel from '../../components/Infographics/RegionalAmountsTradedNationalLevel'
import DataLayout, { DataLayoutChild, DataLayoutViews, useDataLayout } from '../../components/Layouts/DataLayout'
import { Description, GridContainer, GridItem, Root, Title } from './BuyersProvidersSizeSection.styles'

/**
 * The BuyersProvidersSizeSection's container.
 */
const BuyersProvidersSizeSection = () => {
  const dispatch = useDispatch()
  const { endYear, latestUpdate, loading, sectors = [], startYear } = useSelector(state => state.topNavbar)
  const { csv, data } = useSelector(state => state.buyersProvidersSizeSection)
  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: endYear,
    changeCallback: updatedValue =>
      dispatch(onGetBuyersProvidersSizeThunk({ sectorId: sectorValue, year: updatedValue })),
    overwriteCallback: () => endYear
  })
  const { value: sectorValue, onChange: handleChangeSector } = useSelect({
    initialValue: 7,
    changeCallback: updatedValue => dispatch(onGetBuyersProvidersSizeThunk({ sectorId: updatedValue, year: yearValue }))
  })
  const { value: currencyValue, onChange: handleChangeCurrency } = useSwitch()
  const {
    disableCaptureImage,
    disableDownloadFile,
    ref: dataLayoutRef,
    onCaptureImage: handleCaptureImage,
    onChangeView: handleChangeView,
    onDownloadFile: handleDownloadFile
  } = useDataLayout({
    dependencies: [csv, yearValue],
    captureImageCallback: () =>
      exportToImage('buyers-providers-size-columns-chart', `Info_Tamano_Proveedores_${yearValue}`),
    changeViewCallback: updatedView =>
      updatedView === DataLayoutViews.CHART &&
      dispatch(onGetBuyersProvidersSizeThunk({ sectorId: sectorValue, year: yearValue })),
    downloadFileCallback: () => exportToCsv(csv, `Info_Tamano_Proveedores_${yearValue}`)
  })
  const handleDataLabelFormatter = useCallback(function () {
    return `<strong>$${formatAmount(this.y)}</strong>`
  }, [])
  const handleTooltipFormatter = useCallback(
    function () {
      return `<strong>${currencyValue ? CURRENCY_VALUES.USD : CURRENCY_VALUES.CLP} ${formatAmount(
        this.point.y
      )}</strong> transados en el sector <strong>${
        this.point.sector
      }</strong> durante el año <strong>${yearValue}</strong> proveedores cuya clasificación es "<strong>${
        this.x
      }</strong>".`
    },
    [currencyValue, yearValue]
  )
  const yearsOptions = useMemo(() => generateYearsBetween(endYear, startYear), [endYear, startYear])
  const sectorOptions = useMemo(() => sectors.filter(sector => sector.value !== 8), [sectors])
  const enhancedData = useMemo(
    () => ({
      data: data.map(datum => ({
        ...datum,
        percentage: datum.percentage,
        y: currencyValue ? datum?.amountUSD : datum?.amount
      }))
    }),
    [currencyValue, data]
  )

  useEffect(() => {
    if (!endYear || loading) {
      return
    }

    dispatch(onGetBuyersProvidersSizeThunk({ sectorId: sectorValue, year: endYear }))
  }, [endYear, loading])

  return (
    <Root>
      <GridContainer justifyContent="center" padding="0 0 23px">
        <GridItem xs={12}>
          <Title>Tamaño de proveedores</Title>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 28px">
        <GridItem xs={12}>
          <Description>
            Conoce el monto transado con los distintos tipos de empresa con los que ha transado cada sector del Estado
            según el año seleccionado.
          </Description>
        </GridItem>
      </GridContainer>
      <DataLayout
        disableCaptureImage={disableCaptureImage}
        disableDownloadFile={disableDownloadFile}
        header={
          <GridContainer alignItems="center" justifyContent="space-between" spacing={1}>
            <GridItem md={7} xs={12}>
              <GridContainer alignItems="center" justifyContent="space-between" spacing={1}>
                <GridItem md={2} xs={12}>
                  <Select
                    label="AÑO"
                    loading={loading}
                    options={yearsOptions}
                    value={yearValue}
                    onChange={handleChangeYear}
                  />
                </GridItem>
                <GridItem md={4} xs={12}>
                  <Select
                    label="SECTOR DEL ESTADO"
                    loading={loading}
                    options={sectorOptions}
                    value={sectorValue}
                    onChange={handleChangeSector}
                  />
                </GridItem>
                <GridItem md={6} xs={12} />
              </GridContainer>
            </GridItem>
            <GridItem md={2} xs={12}>
              <Switch
                checked={currencyValue}
                color="primary"
                labelLeft="CLP"
                labelRight="USD"
                margin="0 0 0 32px"
                onChange={handleChangeCurrency}
              />
            </GridItem>
          </GridContainer>
        }
        infographic={<RegionalAmountsTradedNationalLevel />}
        noDataText="No hay datos de organismos compradores para los filtros seleccionados."
        ref={dataLayoutRef}
        updatedDate={latestUpdate}
        onCaptureImage={handleCaptureImage}
        onChangeView={handleChangeView}
        onDownloadFile={handleDownloadFile}
      >
        <DataLayoutChild view={DataLayoutViews.CHART}>
          <GridContainer>
            <GridItem xs={12}>
              <ColumnsChart
                categories={['Grande', 'Mediana', 'Micro', 'Pequeña', 'NoClasificado']}
                data={enhancedData}
                dataLabelFormatter={handleDataLabelFormatter}
                id="buyers-providers-size-columns-chart"
                tooltipFormatter={handleTooltipFormatter}
              />
            </GridItem>
          </GridContainer>
        </DataLayoutChild>
        <DataLayoutChild view={DataLayoutViews.TABLE}>
          <GridContainer padding="0 0 20px">
            <GridItem xs={12}>
              <DataTable
                headers={[
                  { label: 'Año', value: 0 },
                  { label: 'Tramaño de proveedor', value: 1 },
                  { label: 'Sector del estado', value: 2 },
                  { label: 'Monto transado', value: 3 },
                  { label: 'Monto transado (USD)', value: 4 }
                ]}
                headersCellsProps={[
                  { width: '5%' },
                  { enableSort: true, width: '15%' },
                  { enableSort: true, width: '20%' },
                  { enableSort: true, width: '20%' },
                  { enableSort: true, width: '20%' }
                ]}
                loading={loading}
                records={csv.slice(1)}
                recordsDecorators={[
                  ([year]) => year,
                  ([, name]) => name,
                  ([, , sector]) => sector,
                  ([, , , amount]) => `$${formatAmount(amount)}`,
                  ([, , , , amountUSD]) => `USD ${formatAmount(amountUSD)}`
                ]}
              />
            </GridItem>
          </GridContainer>
        </DataLayoutChild>
      </DataLayout>
    </Root>
  )
}

export default BuyersProvidersSizeSection
